import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { selectApiUrls } from '../../../redux/slices/apiUrlSlice';
import { createSalesEndpoints } from '../api/salesApi';
import {
  compareRows,
  fallbackStates,
  getPagedRows,
  getPageSummary,
  getSortLabel,
  normalizeLookupName,
  toArray,
} from './salesPageUtils';

const emptyCompanyForm = {
  id: 0,
  name: '',
  contactNumber: '',
  streetAddress: '',
  city: '',
  state: '',
  country: 'United States',
  deleted: false,
  isActive: true,
};

export const normalizeCompany = (company = {}) => ({
  id: Number(company.id ?? company.Id ?? 0),
  name: String(
    company.name ??
      company.Name ??
      `${company.firstName ?? company.FirstName ?? ''} ${company.lastName ?? company.LastName ?? ''}`.trim() ??
      ''
  ),
  contactNumber: String(
    company.contactNumber ??
      company.ContactNumber ??
      company.phoneNumber ??
      company.PhoneNumber ??
      company.phone ??
      company.Phone ??
      ''
  ),
  streetAddress: String(company.streetAddress ?? company.StreetAddress ?? company.address ?? company.Address ?? ''),
  city: String(company.city ?? company.City ?? ''),
  state: String(company.state ?? company.State ?? ''),
  country: String(company.country ?? company.Country ?? 'United States'),
  deleted: Boolean(company.deleted ?? company.Deleted ?? false),
  isActive: Boolean(company.isActive ?? company.IsActive ?? true),
});

const CompanyList = () => {
  const apiUrls = useSelector(selectApiUrls);
  const endpoints = useMemo(() => createSalesEndpoints(apiUrls), [apiUrls]);
  const portalApiRequest = usePortalApiRequest();
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');

  const [companies, setCompanies] = useState([]);
  const [stateOptions, setStateOptions] = useState(fallbackStates);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [companyFormError, setCompanyFormError] = useState('');
  const [savingCompanyForm, setSavingCompanyForm] = useState(false);
  const [companyPendingDelete, setCompanyPendingDelete] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadCompanies = async () => {
      setLoading(true);
      setPageError('');

      try {
        const response = await portalApiRequest({ method: 'GET', url: endpoints.companies.base });
        if (isMounted) {
          setCompanies(toArray(response).map(normalizeCompany));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
          setPageError('Unable to load companies.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadCompanies();

    return () => {
      isMounted = false;
    };
  }, [endpoints, portalApiRequest]);

  useEffect(() => {
    let isMounted = true;

    const loadStates = async () => {
      try {
        const response = await portalApiRequest({ method: 'GET', url: endpoints.lookups.byTypeName('States') });
        const states = toArray(response).map(normalizeLookupName).filter(Boolean);
        if (isMounted && states.length > 0) {
          setStateOptions(states);
        }
      } catch {
        // Keep fallback options.
      }
    };

    loadStates();

    return () => {
      isMounted = false;
    };
  }, [endpoints, portalApiRequest]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm]);

  const filteredCompanies = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return companies;

    return companies.filter((company) =>
      [company.name, company.contactNumber, company.city, company.state, company.country]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [companies, searchTerm]);

  const sortedCompanies = useMemo(
    () => [...filteredCompanies].sort((left, right) => compareRows(left, right, sortField, sortDirection)),
    [filteredCompanies, sortDirection, sortField]
  );

  const pagedCompanies = getPagedRows(sortedCompanies, currentPage, rowsPerPage);
  const { firstRecord, lastRecord, totalPages } = getPageSummary(sortedCompanies.length, currentPage, rowsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const openCompanyModal = (company) => {
    if (!canEditSales) return;
    setCompanyForm(company?.id ? normalizeCompany(company) : { ...emptyCompanyForm });
    setCompanyFormError('');
    setShowCompanyModal(true);
  };

  const closeCompanyModal = () => {
    setShowCompanyModal(false);
    setCompanyForm({ ...emptyCompanyForm });
    setCompanyFormError('');
    setSavingCompanyForm(false);
  };

  const onCompanyFormFieldChange = (field, value) => {
    setCompanyForm((current) => ({ ...current, [field]: value }));
  };

  const validateCompanyForm = () => {
    if (!companyForm.name.trim()) return 'Please enter company name.';
    if (companyForm.name.trim().length < 3) return 'Company name must be longer than 3 characters.';
    if (!companyForm.contactNumber.trim()) return 'Please enter contact number.';
    if (!/^[+ ]?(\d{3}[- ]?){2}\d{4}$/.test(companyForm.contactNumber.trim())) return 'Please enter a valid contact number.';
    if (!companyForm.city.trim()) return 'Please enter city.';
    if (!companyForm.state.trim()) return 'Please select state.';
    if (!companyForm.country.trim()) return 'Please enter country.';

    const duplicateName = companies.some(
      (company) =>
        company.id !== companyForm.id &&
        String(company.name || '').toLowerCase() === companyForm.name.trim().toLowerCase()
    );
    if (duplicateName) return 'Company name must be unique.';
    return '';
  };

  const saveCompanyForm = async () => {
    if (!canEditSales) return;

    const validationError = validateCompanyForm();
    if (validationError) {
      setCompanyFormError(validationError);
      return;
    }

    const payload = normalizeCompany(companyForm);
    const requestPayload = {
      id: payload.id,
      name: payload.name,
      contactNumber: payload.contactNumber,
      streetAddress: payload.streetAddress,
      city: payload.city,
      state: payload.state,
      country: payload.country,
      deleted: payload.deleted,
      isActive: payload.isActive,
    };

    setSavingCompanyForm(true);
    setCompanyFormError('');

    try {
      if (payload.id) {
        const response = await portalApiRequest({
          method: 'PUT',
          url: endpoints.companies.byId(payload.id),
          data: requestPayload,
        });
        const updatedCompany = normalizeCompany({ ...payload, ...(response?.data ?? response ?? {}) });
        setCompanies((current) => current.map((company) => (company.id === updatedCompany.id ? updatedCompany : company)));
      } else {
        const response = await portalApiRequest({
          method: 'POST',
          url: endpoints.companies.base,
          data: requestPayload,
        });
        const createdCompany = normalizeCompany(response?.data ?? response ?? payload);
        setCompanies((current) => [createdCompany, ...current]);
      }
      closeCompanyModal();
    } catch {
      setCompanyFormError('Unable to save company.');
    } finally {
      setSavingCompanyForm(false);
    }
  };

  const removeCompany = async () => {
    if (!canEditSales || !companyPendingDelete?.id) return;

    const companyId = companyPendingDelete.id;
    setDeletingCompany(true);

    try {
      await portalApiRequest({ method: 'DELETE', url: endpoints.companies.byId(companyId) });
      setCompanies((current) => current.filter((company) => company.id !== companyId));
      setCompanyPendingDelete(null);
    } catch {
      setPageError('Unable to delete company.');
    } finally {
      setDeletingCompany(false);
    }
  };

  return (
    <section className="sales-portal-page" aria-labelledby="sales-companies-title">
      <Row className="mb-3">
        <Col>
          <h4 id="sales-companies-title">Company List</h4>
        </Col>
      </Row>

      {!canEditSales && <Alert variant="info">You have read-only access to Sales companies.</Alert>}
      {pageError && <Alert variant="danger">{pageError}</Alert>}

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <div className="position-relative search-input">
              <i
                className="bi bi-search position-absolute"
                style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}
              ></i>
              <Form.Control
                id="sales-company-search"
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search"
                style={{ paddingLeft: '40px' }}
              />
            </div>
          </Form.Group>
        </Col>
        <Col md={6} className="text-end">
          {canEditSales && (
            <Button type="button" className="btn-success addbtnview" onClick={() => openCompanyModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add New Company
            </Button>
          )}
        </Col>
      </Row>

      {loading ? (
        <div className="sales-loading"><Spinner animation="border" /></div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  {[
                    ['name', 'Name'],
                    ['contactNumber', 'Contact Number'],
                    ['city', 'City'],
                    ['state', 'State'],
                    ['country', 'Country'],
                  ].map(([field, label]) => (
                    <th key={field}>
                      <Button
                        type="button"
                        variant="link"
                        className="sales-sort-button"
                        onClick={() => handleSort(field)}
                      >
                        {label} <span>{getSortLabel(sortField, sortDirection, field)}</span>
                      </Button>
                    </th>
                  ))}
                  {canEditSales && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {pagedCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={canEditSales ? 6 : 5}>
                      <div className="sales-no-data">
                        {searchTerm ? 'No companies match your search' : 'No companies found'}
                      </div>
                    </td>
                  </tr>
                ) : (
                  pagedCompanies.map((company) => (
                    <tr key={company.id || company.name}>
                      <td><strong>{company.name}</strong></td>
                      <td>{company.contactNumber}</td>
                      <td>{company.city}</td>
                      <td>{company.state}</td>
                      <td>{company.country}</td>
                      {canEditSales && (
                        <td style={{ textAlign: 'center' }}>
                          <div className="d-inline-flex align-items-center" style={{ gap: '10px' }}>
                            <button
                              type="button"
                              className="edit-icon"
                              title="Edit"
                              onClick={(event) => {
                                event.preventDefault();
                                openCompanyModal(company);
                              }}
                            >
                              <i className="bi bi-pencil-square"></i>
                            </button>
                            <button
                              type="button"
                              className="edit-icon"
                              title="Delete"
                              onClick={(event) => {
                                event.preventDefault();
                                setCompanyPendingDelete(company);
                              }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          </div>
          <div className="sales-pagination d-flex justify-content-between align-items-center p-3 border-top">
            <div>Showing {firstRecord} to {lastRecord} of {sortedCompanies.length} entries</div>
            <div className="d-flex align-items-center">
              <Form.Select
                size="sm"
                className="me-2"
                style={{ width: '90px' }}
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
              >
                {[10, 25, 50].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Form.Select>
              <Button size="sm" variant="outline-secondary" className="me-2" disabled={currentPage <= 1} onClick={() => setCurrentPage((page) => page - 1)}>
                Prev
              </Button>
              <span className="me-2">Page {currentPage} of {totalPages}</span>
              <Button size="sm" variant="outline-secondary" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((page) => page + 1)}>
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      <Modal show={showCompanyModal} onHide={closeCompanyModal} backdrop="static">
        <Modal.Header closeButton={!savingCompanyForm}>
          <Modal.Title>{companyForm.id ? 'Edit Company' : 'Add Company'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {companyFormError && <Alert variant="danger">{companyFormError}</Alert>}
          <Row className="g-3">
            <Col md={12}>
              <Form.Label>Company Name *</Form.Label>
              <Form.Control value={companyForm.name} onChange={(event) => onCompanyFormFieldChange('name', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Contact Number *</Form.Label>
              <Form.Control value={companyForm.contactNumber} onChange={(event) => onCompanyFormFieldChange('contactNumber', event.target.value)} />
            </Col>
            <Col md={6}>
              <Form.Label>Street Address</Form.Label>
              <Form.Control value={companyForm.streetAddress} onChange={(event) => onCompanyFormFieldChange('streetAddress', event.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label>City *</Form.Label>
              <Form.Control value={companyForm.city} onChange={(event) => onCompanyFormFieldChange('city', event.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label>State *</Form.Label>
              <Form.Select value={companyForm.state} onChange={(event) => onCompanyFormFieldChange('state', event.target.value)}>
                <option value="">Select State</option>
                {stateOptions.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Country *</Form.Label>
              <Form.Control value={companyForm.country} onChange={(event) => onCompanyFormFieldChange('country', event.target.value)} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCompanyModal} disabled={savingCompanyForm}>Cancel</Button>
          <Button variant="primary" onClick={saveCompanyForm} disabled={savingCompanyForm}>
            {savingCompanyForm ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(companyPendingDelete)} onHide={() => setCompanyPendingDelete(null)} backdrop="static" centered>
        <Modal.Header closeButton={!deletingCompany}>
          <Modal.Title>Delete Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>Delete {companyPendingDelete?.name || 'this company'}?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCompanyPendingDelete(null)} disabled={deletingCompany}>Cancel</Button>
          <Button variant="danger" onClick={removeCompany} disabled={deletingCompany}>
            {deletingCompany ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default CompanyList;
